/**
 * F16 — Photo upload dropzone.
 *
 * Drag-and-drop + file picker entrypoint for the WorkDetail photo
 * grid. Validates magic bytes + size on selection, shows a preview
 * thumbnail (already resized to 180px so the previewing itself is
 * the upload-time work, not throwaway), lets the user drop staged
 * files individually, then runs `sendPhotoUpsert` sequentially
 * with a "X of N" progress hint.
 *
 * Sequential upload — not parallel — because a 5-photo batch
 * runs 5 × (canvas decode + JPEG encode + AES-CBC encrypt +
 * HTTP POST) and the canvas/encrypt halves block the main thread
 * for ~50–150ms each. Doing them concurrently produces visible
 * lag on the WorkDetail's other interactions; sequential keeps
 * the rest of the UI responsive in exchange for `N × work` total
 * time, which is acceptable for the typical 1–3 photo batch.
 *
 * The component is "fire and report" — once an upload succeeds the
 * `onUploaded` callback fires so the parent can refetch its photo
 * list (the live-sync emitter would do it eventually, but a
 * synchronous refetch removes the wait). On failure the error is
 * surfaced inline next to the failed thumbnail; the rest of the
 * batch keeps going.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Plus, Upload, X } from 'lucide-react';

import { sendPhotoUpsert } from '../sync/uploadPhoto';
import {
  MAX_PHOTO_BYTES,
  resizeImage,
  validateMagicBytes,
  validateSize,
} from '../utils/imageProcessing';
import type { WebIdentity } from '../store/keys';
import type { Photo } from '../types';

import './PhotoUploadDropzone.css';

interface PhotoUploadDropzoneProps {
  workId: string;
  identity: WebIdentity;
  /** Fired once per uploaded photo so the parent can refresh. */
  onUploaded?: (photo: Photo) => void;
  /** Test-only — drops the upload pipeline call so the staging
   *  + preview machinery can be unit-tested without crypto + fetch. */
  uploader?: typeof sendPhotoUpsert;
}

interface StagedPhoto {
  /** Stable key for React; not the eventual Photo.id. */
  key: string;
  file: File;
  /** Resized 180px JPEG dataURL — used for the inline preview. */
  previewDataUrl: string;
  /** When validation failed this carries the message; the row
   *  renders disabled with the error inline. */
  error?: string;
}

const STATUS_IDLE = 'idle';
const STATUS_UPLOADING = 'uploading';
type Status = typeof STATUS_IDLE | typeof STATUS_UPLOADING;

const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,image/*';

export function PhotoUploadDropzone({
  workId,
  identity,
  onUploaded,
  uploader = sendPhotoUpsert,
}: PhotoUploadDropzoneProps) {
  const [staged, setStaged] = useState<StagedPhoto[]>([]);
  const [status, setStatus] = useState<Status>(STATUS_IDLE);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Drag enter/leave fires on every child crossing in Chromium —
  // counter-based tracking is the well-known fix.
  const dragCounter = useRef(0);

  const handleFiles = useCallback(
    async (incoming: FileList | File[] | null) => {
      if (!incoming || incoming.length === 0) return;
      setGlobalError(null);
      const files = Array.from(incoming);

      // Cap batch size: 10 is the sweet spot — sequential upload of
      // 10 × ~200 KB JPEGs runs in ~10s on a typical laptop, longer
      // batches risk session timeouts and tax the user's patience
      // without an obvious benefit (they can drop more after the
      // first batch finishes).
      if (files.length > 10) {
        setGlobalError('Máximo 10 fotos por lote. Sube las primeras y añade el resto luego.');
        return;
      }

      const next: StagedPhoto[] = [];
      for (const file of files) {
        const key = `${file.name}:${file.size}:${file.lastModified}:${Math.random().toString(36).slice(2, 8)}`;
        try {
          await validateMagicBytes(file);
          validateSize(file);
          const preview = await resizeImage(file, 180, 0.6);
          next.push({ key, file, previewDataUrl: preview.dataUrl });
        } catch (err) {
          next.push({
            key,
            file,
            previewDataUrl: '',
            error: err instanceof Error ? err.message : 'Error al procesar la imagen.',
          });
        }
      }
      setStaged((prev) => [...prev, ...next]);
    },
    [],
  );

  const removeStaged = useCallback((key: string) => {
    setStaged((prev) => prev.filter((s) => s.key !== key));
  }, []);

  const clearAll = useCallback(() => {
    setStaged([]);
    setGlobalError(null);
  }, []);

  // F16 PR2 — paste-from-clipboard. A common workflow on Windows /
  // Mac is "screenshot tool → Cmd+V": the clipboard carries the
  // image as a File when ClipboardEvent.clipboardData has an item
  // of kind 'file' with type 'image/*'. We listen at document
  // level (so the user doesn't have to focus the dropzone) but
  // gate by `target` to avoid stealing pastes from input fields.
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      }
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.kind === 'file' && it.type.startsWith('image/')) {
          const file = it.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length === 0) return;
      e.preventDefault();
      void handleFiles(files);
    }
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [handleFiles]);

  const handleUpload = useCallback(async () => {
    const eligible = staged.filter((s) => !s.error);
    if (eligible.length === 0) return;
    setStatus(STATUS_UPLOADING);
    setProgress({ current: 0, total: eligible.length });
    setGlobalError(null);

    const failures: StagedPhoto[] = [];
    for (let i = 0; i < eligible.length; i++) {
      const s = eligible[i];
      setProgress({ current: i + 1, total: eligible.length });
      try {
        const result = await uploader(s.file, workId, identity);
        onUploaded?.(result.photo);
      } catch (err) {
        failures.push({
          ...s,
          error: err instanceof Error ? err.message : 'Error de red al subir.',
        });
      }
    }

    setStatus(STATUS_IDLE);
    setProgress(null);
    if (failures.length > 0) {
      // Keep failed rows visible so the user can retry; drop the rows
      // that succeeded so the dropzone empties out for them.
      const failedKeys = new Set(failures.map((f) => f.key));
      setStaged((prev) =>
        prev
          .map((s) => (failedKeys.has(s.key) ? failures.find((f) => f.key === s.key) ?? s : s))
          .filter((s) => failedKeys.has(s.key) || s.error),
      );
      setGlobalError(
        failures.length === 1
          ? '1 foto no se subió. Revisa el error y reintenta.'
          : `${failures.length} fotos no se subieron. Revisa los errores y reintenta.`,
      );
    } else {
      setStaged([]);
    }
  }, [staged, workId, identity, onUploaded, uploader]);

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current += 1;
    if (dragCounter.current === 1) setIsDragOver(true);
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setIsDragOver(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragOver(false);
    void handleFiles(e.dataTransfer.files);
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    void handleFiles(e.target.files);
    // Reset so the same file can be re-selected after a remove.
    e.target.value = '';
  };

  const eligibleCount = staged.filter((s) => !s.error).length;
  const dropZoneClass = `pud-dropzone${isDragOver ? ' pud-dropzone--over' : ''}`;
  const isUploading = status === STATUS_UPLOADING;
  const maxMb = (MAX_PHOTO_BYTES / (1024 * 1024)).toFixed(0);

  return (
    <div className="pud-root" data-testid="photo-upload-dropzone">
      <div
        className={dropZoneClass}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-testid="photo-upload-target"
      >
        <ImageIcon size={28} strokeWidth={2} aria-hidden="true" className="pud-illu" />
        <p className="pud-instr">
          Arrastra una foto aquí o{' '}
          <button
            type="button"
            className="pud-link"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            data-testid="photo-upload-pick-link"
          >
            elige una desde tu equipo
          </button>
          .
        </p>
        <p className="pud-help">
          JPEG, PNG, WebP o HEIC · máximo {maxMb} MB · se redimensiona y cifra antes de
          subir. También puedes pegar (<kbd>Ctrl/Cmd</kbd>+<kbd>V</kbd>) una captura de
          pantalla.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="pud-file"
          onChange={onFileInputChange}
          data-testid="photo-upload-input"
        />
      </div>

      {globalError && (
        <p className="pud-error" role="alert" data-testid="photo-upload-error">
          {globalError}
        </p>
      )}

      {staged.length > 0 && (
        <div className="pud-staged" data-testid="photo-upload-staged">
          <ul className="pud-staged-list">
            {staged.map((s) => (
              <li
                key={s.key}
                className={`pud-staged-item${s.error ? ' pud-staged-item--error' : ''}`}
                data-testid={`photo-upload-staged-${s.key}`}
              >
                {s.previewDataUrl ? (
                  <img src={s.previewDataUrl} alt="" className="pud-staged-thumb" />
                ) : (
                  <div className="pud-staged-thumb pud-staged-thumb--missing" aria-hidden="true">
                    <ImageIcon size={20} strokeWidth={2} />
                  </div>
                )}
                <div className="pud-staged-meta">
                  <span className="pud-staged-name" title={s.file.name}>
                    {s.file.name}
                  </span>
                  {s.error && (
                    <span className="pud-staged-error" role="alert">
                      {s.error}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="pud-staged-remove"
                  onClick={() => removeStaged(s.key)}
                  aria-label={`Quitar ${s.file.name} del lote`}
                  disabled={isUploading}
                  data-testid={`photo-upload-remove-${s.key}`}
                >
                  <X size={16} strokeWidth={2} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>

          <div className="pud-staged-actions">
            <button
              type="button"
              className="pud-btn pud-btn--ghost"
              onClick={clearAll}
              disabled={isUploading}
              data-testid="photo-upload-clear"
            >
              Vaciar
            </button>
            <button
              type="button"
              className="pud-btn pud-btn--primary"
              onClick={handleUpload}
              disabled={isUploading || eligibleCount === 0}
              data-testid="photo-upload-submit"
            >
              {isUploading && progress ? (
                <>
                  <Upload size={16} strokeWidth={2} aria-hidden="true" />
                  Subiendo {progress.current} de {progress.total}…
                </>
              ) : (
                <>
                  <Plus size={16} strokeWidth={2} aria-hidden="true" />
                  Subir {eligibleCount === 1 ? 'foto' : `${eligibleCount} fotos`}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
